import { Paper, Stack, Text } from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Practice, User } from './types/rbac';
import { heldRolesForUser, ROLE_LABELS } from './types/rbac';
import { UserFilters } from './components/UserFilters';
import { UserTable, lastActiveSortKey } from './components/UserTable';

interface UsersPageProps {
  /** Full list to display: all users (platform) or practice users (practice-scoped). */
  users: User[];
  /** Practices for filter dropdown and Create User modal (platform mode). */
  practices?: Practice[];
  /** Patient ids listed under each practice, used because clients may belong to multiple practices. */
  clientIdsByOrganizationId?: Record<string, string[]>;
  /** When set, UI is scoped to this practice: hide practice filter, table without Practice column, Create User with this practice. */
  scopePracticeId?: string;
  scopePractice?: Practice;
  onViewUser: (user: User) => void;
  onUpdateUser: (id: string, updates: Partial<User>) => void;
  onCreateUser: () => void;
  onResendInvite?: (email: string) => Promise<void>;
  createButtonLabel?: string;
  /** Opens the full client intake wizard (practice-scoped admin flow). */
  onCreateClient?: () => void;
  hideCreateUserButton?: boolean;
  /** Clients tab: omit Role column and All Roles filter. */
  hideRoles?: boolean;
}

export function userMatchesPracticeFilter(
  user: User,
  practiceFilter: string[],
  clientIdsByOrganizationId: Record<string, string[]>,
  allPracticeIds: string[] = [],
): boolean {
  if (user.role !== 'client') return practiceFilter.includes(user.practiceId);
  const allPracticesSelected =
    allPracticeIds.length > 0 &&
    allPracticeIds.every((id) => practiceFilter.includes(id));
  if (allPracticesSelected) return true;
  return practiceFilter.some((practiceId) =>
    clientIdsByOrganizationId[practiceId]?.includes(user.id),
  );
}

export function UsersPage({
  users,
  practices = [],
  clientIdsByOrganizationId = {},
  scopePracticeId,
  scopePractice,
  onViewUser,
  onUpdateUser,
  onCreateUser,
  onResendInvite,
  createButtonLabel,
  onCreateClient,
  hideCreateUserButton,
  hideRoles = false,
}: UsersPageProps): JSX.Element {
  // Currently unused, but kept for future practice-scoped header UI.
  void scopePractice;

  const [searchQuery, setSearchQuery] = useState('');
  const [practiceFilter, setPracticeFilter] = useState<string[]>([]);
  const [roleFilter, setRoleFilter] = useState<string[]>(() => Object.keys(ROLE_LABELS));
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const practiceFilterSeeded = useRef(false);
  const roleFilterSeeded = useRef(Object.keys(ROLE_LABELS).length > 0);

  useEffect(() => {
    if (practiceFilterSeeded.current || practices.length === 0) return;
    setPracticeFilter(practices.map((p) => p.id));
    practiceFilterSeeded.current = true;
  }, [practices]);

  useEffect(() => {
    if (hideRoles || roleFilterSeeded.current) return;
    setRoleFilter(Object.keys(ROLE_LABELS));
    roleFilterSeeded.current = true;
  }, [hideRoles]);

  const [sortColumn, setSortColumn] = useState<'name' | 'email' | 'lastActive'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const filteredUsers = useMemo(() => {
    return users.filter((user) => {
      const matchesSearch =
        user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.legalName?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false) ||
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.practiceName?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false);

      const matchesPractice =
        scopePracticeId != null || practices.length === 0 || !practiceFilterSeeded.current
          ? true
          : userMatchesPracticeFilter(
              user,
              practiceFilter,
              clientIdsByOrganizationId,
              practices.map((practice) => practice.id),
            );

      const matchesRole =
        hideRoles
          ? true
          : heldRolesForUser(user).some((role) => roleFilter.includes(role));
      const matchesStatus = statusFilter ? user.status === statusFilter : true;

      return matchesSearch && matchesPractice && matchesRole && matchesStatus;
    });
  }, [users, searchQuery, practiceFilter, roleFilter, statusFilter, scopePracticeId, hideRoles, clientIdsByOrganizationId, practices]);

  const sortedUsers = useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'name') {
        comparison = a.name.localeCompare(b.name);
      } else if (sortColumn === 'email') {
        comparison = a.email.localeCompare(b.email);
      } else if (sortColumn === 'lastActive') {
        comparison = lastActiveSortKey(a.lastActive) - lastActiveSortKey(b.lastActive);
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredUsers, sortColumn, sortDirection]);

  const isPlatformView = scopePracticeId == null;

  const handleSort = (column: typeof sortColumn) => {
    setSortColumn((prevCol) => {
      if (prevCol === column) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        return prevCol;
      }
      setSortDirection('asc');
      return column;
    });
  };

  return (
    <Stack gap="md">
      <UserFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        practices={practices}
        practiceFilter={practiceFilter}
        onPracticeChange={setPracticeFilter}
        roleFilter={roleFilter}
        onRoleChange={setRoleFilter}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        showPracticeFilter={isPlatformView}
        hideRoles={hideRoles}
        onCreateUser={onCreateUser}
        createButtonLabel={createButtonLabel}
        onCreateClient={onCreateClient}
        hideCreateUserButton={hideCreateUserButton}
      />

      <Paper p="lg" withBorder>
        {sortedUsers.length === 0 ? (
          <Text c="gray.6" ta="center" py="xl">
            No users found
          </Text>
        ) : (
          <UserTable
            users={sortedUsers}
            onUpdateUser={onUpdateUser}
            onResendInvite={onResendInvite}
            onViewUser={onViewUser}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            hideRoles={hideRoles}
          />
        )}
      </Paper>
    </Stack>
  );
}
